import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  ClipboardDocumentListIcon,
  PlusCircleIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link, usePage } from "@inertiajs/inertia-react";
import NoticeMessage from "../Components/Flash/NoticeMessage";
import Logo from "../Components/Logo";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Layout for the Tradesman Domestic customer portal. Deliberately separate
// from Layout.js: that component is keyed off user.main_account_type, which
// customers (a separate auth scope) do not have.
//
// Visually this mirrors Layout.js (the tradesman-side chrome) as closely as
// possible — same dark gray-800 sidebar, same real wordmark, same white
// content area — so customers land on something that reads as a
// professional trade-services product, not a different, cartoonish brand.
export default function CustomerLayout({ children, title, subtitle, primary_button }) {
  const { customer } = usePage().props;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    {
      name: "My Requests",
      href: "/domestic/requests",
      icon: ClipboardDocumentListIcon,
      current:
        window.location.pathname.includes("domestic/requests") &&
        !window.location.pathname.includes("new"),
    },
    {
      name: "Request a home visit",
      href: "/domestic/requests/new",
      icon: PlusCircleIcon,
      current: window.location.pathname.includes("domestic/requests/new"),
    },
  ];

  const sidebarNav = (
    <nav className="mt-5 space-y-1 px-2">
      {navigation.map((item) => (
        <a
          key={item.name}
          href={item.href}
          className={classNames(
            item.current
              ? "bg-gray-900 text-white"
              : "text-gray-300 hover:bg-gray-700 hover:text-white",
            "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
          )}
        >
          <item.icon
            className={classNames(
              item.current ? "text-gray-300" : "text-gray-400 group-hover:text-gray-300",
              "mr-3 h-6 w-6 flex-shrink-0"
            )}
            aria-hidden="true"
          />
          {item.name}
        </a>
      ))}
    </nav>
  );

  const signOutBlock = (
    <div className="flex flex-shrink-0 bg-gray-700 p-4">
      <div className="group block w-full flex-shrink-0">
        <div className="flex items-center">
          <UserCircleIcon className="h-9 w-9 text-gray-400" aria-hidden="true" />
          <div className="ml-3 flex flex-col">
            <p className="text-sm font-medium text-white">{customer?.name}</p>
            <form action="/customer/signout" method="POST">
              <button
                type="submit"
                className="mt-0.5 text-xs font-medium text-gray-300 group-hover:text-gray-200"
              >
                Sign out
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-800">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full border-2 border-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                  <div className="flex flex-shrink-0 items-center px-4">
                    <Logo />
                  </div>
                  {sidebarNav}
                </div>
                {signOutBlock}
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-gray-800">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <Logo />
            </div>
            {sidebarNav}
          </div>
          {signOutBlock}
        </div>
      </div>

      <div className="flex flex-1 flex-col md:pl-64">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white py-2 pl-3 pr-1 sm:pl-4 sm:pr-2 md:hidden">
          <Logo className="h-6 w-auto" />
          <button
            type="button"
            className="inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <main className="min-h-screen flex-1 bg-gray-50">
          <div className="py-8">
            <div className="mx-auto flex max-w-6xl items-start justify-between gap-4 px-4 sm:px-6 md:px-8">
              <div className="min-w-0">
                <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
                {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
              </div>
              {primary_button && (
                <Link
                  href={primary_button.link}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700"
                >
                  {primary_button.icon && <primary_button.icon className="h-4 w-4" />}
                  {primary_button.name}
                </Link>
              )}
            </div>
            <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6 md:px-8">
              <NoticeMessage />
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
